import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme, GTFonts, PoppinsFonts, LatoFonts } from '../../theme';
import { formatWithCommas, s, fs, v } from '../../utils/libs';

type TooltipItem = {
  color: string;
  label?: string;
  value: number;
  formatter?: (n: number) => string;
};

type Props = {
  header?: string;
  items: TooltipItem[];
  note?: string;
  maxItems?: number;
};

const ChartTooltip: React.FC<Props> = ({ header, items, note, maxItems }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const displayItems = useMemo(
    () => (maxItems ? items.slice(0, maxItems) : items),
    [items, maxItems],
  );

  return (
    <View style={styles.container} pointerEvents="none">
      {header ? <Text style={styles.header}>{header}</Text> : null}
      {displayItems.map((it, idx) => (
        <View key={idx} style={styles.row}>
          <View style={[styles.dot, { backgroundColor: it.color }]} />
          {it.label ? (
            <Text style={styles.label} numberOfLines={1}>
              {it.label}
            </Text>
          ) : null}
          <Text style={styles.value} numberOfLines={1}>
            {it.formatter
              ? it.formatter(it.value)
              : formatWithCommas(Number(it.value || 0), 0)}
          </Text>
        </View>
      ))}
      {note ? (
        <Text style={styles.note} numberOfLines={2}>
          {note}
        </Text>
      ) : null}
    </View>
  );
};

export default ChartTooltip;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.sm,
      paddingVertical: s(6),
      paddingHorizontal: s(8),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
      maxWidth: s(220),
      ...theme.shadows.sm,
    },
    header: {
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      marginBottom: v(6),
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: v(4),
    },
    dot: {
      width: s(8),
      height: s(8),
      borderRadius: s(4),
      marginRight: s(6),
    },
    label: {
      color: theme.colors.textSecondary,
      fontFamily: PoppinsFonts.regular,
      fontSize: fs(11),
      marginRight: s(8),
      flexShrink: 1,
    },
    value: {
      marginLeft: 'auto',
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
    },
    note: {
      color: theme.colors.textSecondary,
      fontFamily: PoppinsFonts.regular,
      fontSize: fs(10),
      marginTop: v(6),
    },
  });
